import { FunctionComponent, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { TabPanel } from "@/components/Tabs/Tabs";

import { useMarkCopywritingFrameworkReviewedMutation } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";

import { CopywritingSettings } from "../CopywritingSettings/CopywritingSettings";
import { CopywritingFrameworks } from "../CopywritingFrameworks/CopywritingFrameworks";

enum CopywritingSubTab {
	GENERAL = "GENERAL",
	FRAMEWORKS = "FRAMEWORKS",
}

export const CopywritingTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`copywritingFrameworks.subTabs.${key}`), [t]);
	const [searchParams, setSearchParams] = useSearchParams();

	const [markCopywritingReviewed] = useMarkCopywritingFrameworkReviewedMutation();

	// Visiting the section counts as reviewing it — clears the left-nav orange dot.
	useEffect(() => {
		markCopywritingReviewed();
	}, [markCopywritingReviewed]);

	const activeSubTab =
		(searchParams.get("copywritingTab") as CopywritingSubTab) || CopywritingSubTab.GENERAL;

	const handleSubTabChange = useCallback(
		(value: string) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("copywritingTab", value);

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const tabs = [
		{ label: ts("general"), value: CopywritingSubTab.GENERAL },
		{ label: ts("frameworks"), value: CopywritingSubTab.FRAMEWORKS },
	];

	return (
		<div className="flex flex-col h-full w-full">
			<PageHeader
				activeTab={activeSubTab}
				tabs={tabs}
				title={t("copywritingFrameworks.pageTitle")}
				onTabChange={handleSubTabChange}
			/>

			<TabPanel activeTab={activeSubTab} value={CopywritingSubTab.GENERAL}>
				<CopywritingSettings />
			</TabPanel>

			<TabPanel activeTab={activeSubTab} value={CopywritingSubTab.FRAMEWORKS}>
				<CopywritingFrameworks />
			</TabPanel>
		</div>
	);
};
